import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getAttendants, getUser } from '@/api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, ContentBlock, Loader, Typography } from '@/components';
import { CheckboxField, Flex, Grid, View } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useStyles } from './styles';
import {
  formatDateWithTime,
  formatPhoneNumber,
  getFilteredScans,
  getSuccessfulScan,
  useIframeMessageHandler
} from '@/utils';
import { CustomerAccounts, ScanStatus } from '@/constants';
import { RefreshButton } from '@/components/RefreshButton';
import { QRCodeButton } from '@/components/QRCodeButton';
import { CustomerQRCode } from '@/components/CustomerQRCode';
import { SizeRecommendations } from '@/components/SizeRecommendations';
import { ScanMeasurements } from '@/components/ScanMeasurements';
import { useAssetsData } from '@/zustand/useAssetsData';
import { useAttendantData } from '@/zustand/useAttendantData';
import { useFrameData } from '@/zustand/useFrameData';
import { useAlertData } from '@/zustand/useAlertData';
import { useAppData } from '@/zustand/useAppData';
import ReactGA from 'react-ga4';
import { QuestionnaireIframe } from '@/components/QuestionnaireIframe';

export enum AdditionalInfoStates {
  QRCode = 'QR Code',
  SizeRecommendations = 'Size Recommendations',
  ScanMeasurements = 'Scan Measurements'
}

const checkHAWGValid = (currentCustomer) => {
  const { height, dateOfBirth, weight, gender } = currentCustomer || {};

  return !!height && !!dateOfBirth && !!weight && !!gender;
};

export const Customer = () => {
  const setAlert = useAlertData((store) => store.setAlertData);
  const inIframe = useFrameData((store) => store.isInIframe);

  const {
    asset_list: { confirm_deposit_url },
    behavior_list: {
      configurator_url,
      configurator_eventid_lookup_list,
      customer_account_handle,
      confirm_deposit_checkbox,
      attendant_flow,
      rtw_measures,
      fit_preference_url
    }
  } = useAssetsData((store) => store.assetsData);
  const attendantToken = useAttendantData((store) => store.token);
  const user = useAttendantData((store) => store.user);
  const { id } = useAppData((store) => store.appData);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [previousScan, setPreviousScan] = useState(null);
  const [successfulScan, setSuccessfulScan] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [additionalInfoState, setAdditionalInfoState] =
    useState<AdditionalInfoStates | null>(null);
  const [isAdditionalInfoFullScreen, setIsAdditionalInfoFullScreen] =
    useState<boolean>(false);
  const [isShowQuestionnaire, setIsShowQuestionnaire] = useState(false);
  const styles = useStyles();
  const navigate = useNavigate();

  const [shopLink, setShopLink] = useState(configurator_url);
  const [confirmDepositValue, setConfirmDepositValue] = useState(false);

  const isObjectEmpty = (obj) => {
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      return Object.keys(obj).length === 0;
    }
    return true;
  };
  const getAttendantId = async () => {
    const attendants = await getAttendants(id);
    const currentAttendant = attendants?.find(
      (a) => a.email === user.email && a.name === user.attendant_name
    );
    if (currentAttendant) {
      return currentAttendant?.employeeId;
    }
  };

  const isPreviousScanComplete =
    previousScan !== null && previousScan.status === ScanStatus.Complete;

  const getScans = useCallback(async (customerData: any) => {
    setRefreshLoading(true);
    const filteredScansData = await getFilteredScans(params.customerId);
    const successfulScanData = getSuccessfulScan(filteredScansData);

    if (successfulScanData && fit_preference_url) {
      showQuestionnaire(customerData?.id);
    }

    if (filteredScansData.length > 0) {
      // loop through scans and find the first complete scan
      for (let i = 0; i < filteredScansData.length; i++) {
        if (filteredScansData[i].status === ScanStatus.Complete) {
          setPreviousScan(filteredScansData[i]);
          break;
        }
      }
    }

    if (shopLink) {
      if (successfulScanData) {
        setSuccessfulScan(successfulScanData);
      }
      const user = await Auth.currentUserInfo();
      const email = user?.attributes?.email;
      const eventId = configurator_eventid_lookup_list[email];

      let completeLink = shopLink
        .replace('{emailAddress}', encodeURIComponent(customerData.email))
        .replace('{userid}', customerData.id)
        .replace('{eventId}', eventId)
        .replace('{firstName}', customerData.firstName)
        .replace('{lastName}', customerData.lastName);

      // if HAWG is not valid, then this is a legacy account
      // pass through the placeholder "legacy_scan" for scan id
      if (!checkHAWGValid(customerData)) {
        completeLink = completeLink.replace('{scanId}', 'legacy_scan');
      } else if (filteredScansData[0]) {
        completeLink = completeLink.replace(
          '{scanId}',
          filteredScansData[0].id
        );
      } else {
        completeLink = completeLink.replace('&scan_id={scanId}', '');
      }

      if (attendant_flow) {
        const attendant_id = await getAttendantId();
        completeLink = completeLink.replace(
          '&token={attendantToken}',
          `&token=${attendantToken}&attendant_id=${attendant_id}`
        );
      } else {
        completeLink = completeLink.replace('&token={attendantToken}', '');
      }

      setShopLink(completeLink);
    }
    setRefreshLoading(false);
  }, []);

  useIframeMessageHandler(currentCustomer, successfulScan?.id);

  const contentChangeHandler = (state: AdditionalInfoStates | null) => {
    switch (state) {
      case AdditionalInfoStates.QRCode:
        return <CustomerQRCode currentCustomer={currentCustomer} />;
      case AdditionalInfoStates.SizeRecommendations:
        return (
          <SizeRecommendations
            scanId={previousScan?.id}
            setFullScreen={setIsAdditionalInfoFullScreen}
          />
        );
      case AdditionalInfoStates.ScanMeasurements:
        return <ScanMeasurements />;
      default:
        return null;
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const customerData = await getUser(params.customerId);
      if (customerData.status) {
        if (customerData.status === 403) {
          setAlert({
            text: 'This account is not sharing to your company.',
            variation: 'error'
          });
        } else if (customerData.status === 404) {
          setAlert({
            text: 'This account was not found.',
            variation: 'error'
          });
        }
      } else {
        await getScans(customerData);
        setCurrentCustomer(customerData);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    let interval;

    if (!loading) {
      interval = setInterval(() => {
        getScans(currentCustomer);
      }, 60000);
    }

    return () => clearInterval(interval);
  }, [loading]);

  function showQuestionnaire(customerId: string) {
    if (searchParams.get('newUser') === 'true') {
      const seenUsers = JSON.parse(localStorage.getItem('surveySeen') || '{}');
      if (!seenUsers[customerId]) {
        setIsShowQuestionnaire(true);
      }
    }
  }

  if (loading) {
    return <Loader />;
  }

  const confirmDepositLabel = (
    <div className={styles.confirmDepositLabel}>
      Accept{' '}
      <a target="_blank" href={confirm_deposit_url} rel="noreferrer">
        Made to Measure Deposit*
      </a>
    </div>
  );

  if (!currentCustomer) return null;

  return (
    <>
      <ContentBlock
        additionalInfoLargeSize={isAdditionalInfoFullScreen}
        hasBackButton
        showAdditionalInfo={!!additionalInfoState}
        hideAdditionalInfo={(v) => {
          setAdditionalInfoState(v);
          setIsAdditionalInfoFullScreen(false);
        }}
        additionalInfoTitle={additionalInfoState}
        additionalInfo={contentChangeHandler(additionalInfoState)}
      >
        <Grid
          templateColumns="1fr"
          columnGap="3rem"
          className={styles.customer}
        >
          <View columnSpan={[2, 2, 1]} className={styles.content}>
            <Flex className={styles.customerHeader}>
              <RefreshButton
                onClick={() => getScans(currentCustomer)}
                loading={refreshLoading}
              />
              <QRCodeButton
                style={{ marginLeft: 10 }}
                open={additionalInfoState === AdditionalInfoStates.QRCode}
                onClick={(v) => {
                  ReactGA.event('opened_qr_code');
                  setAdditionalInfoState(v);
                }}
              />
            </Flex>
            <Typography component="p">Welcome</Typography>
            <Typography component="h2" className={styles.title}>
              {currentCustomer.firstName} {currentCustomer.lastName}
            </Typography>
            <div>
              <Button
                text="Edit Profile"
                buttonType="dark"
                size={'small'}
                className={styles.button}
                onClick={() =>
                  navigate(
                    `/createCustomer/${params.customerId}/${params.accountId}`
                  )
                }
              />
            </div>
            <div className={styles.personalInfo}>
              {customer_account_handle ===
              CustomerAccounts.ThirdPartyAccountId ? (
                <Typography component="p" className={styles.subtitle}>
                  <span>Subject ID:</span> {decodeURI(params.accountId)}
                </Typography>
              ) : (
                <>
                  <Typography component="p" className={styles.subtitle}>
                    <span>Phone:</span>{' '}
                    {currentCustomer.phone
                      ? formatPhoneNumber(currentCustomer.phone)
                      : '-'}
                  </Typography>
                  <Typography component="p" className={styles.subtitle}>
                    <span>Email:</span> {currentCustomer.email || '-'}
                  </Typography>
                </>
              )}
            </div>
            <div className={styles.actionBlock}>
              <Button
                text={
                  <>
                    <span className={styles.desktopText}>
                      {AdditionalInfoStates.ScanMeasurements}
                    </span>
                    <span className={styles.mobileText}>Measurements</span>
                  </>
                }
                buttonType={
                  additionalInfoState === AdditionalInfoStates.ScanMeasurements
                    ? 'secondary'
                    : 'dark'
                }
                size={'small'}
                className={styles.button}
                onClick={() => {
                  ReactGA.event('opened_scan_measurements');
                  setAdditionalInfoState(AdditionalInfoStates.ScanMeasurements);
                }}
              />
              <>
                {!isObjectEmpty(rtw_measures) && (
                  <Button
                    text={
                      <>
                        <span className={styles.desktopText}>
                          {AdditionalInfoStates.SizeRecommendations}
                        </span>
                        <span className={styles.mobileText}>
                          Recommendations
                        </span>
                      </>
                    }
                    buttonType={
                      additionalInfoState ===
                      AdditionalInfoStates.SizeRecommendations
                        ? 'secondary'
                        : 'dark'
                    }
                    size={'small'}
                    className={styles.button}
                    onClick={() => {
                      ReactGA.event('opened_size_recommendations');
                      setAdditionalInfoState(
                        AdditionalInfoStates.SizeRecommendations
                      );
                    }}
                    isDisabled={!isPreviousScanComplete}
                  />
                )}
                {fit_preference_url && (
                  <Button
                    style={{ width: '46%' }}
                    text={'Fit Preferences'}
                    buttonType={'dark'}
                    size={'small'}
                    className={styles.button}
                    onClick={() => {
                      ReactGA.event('go_to_fit_preferences');
                      window.open(
                        `${fit_preference_url}&firstName=${currentCustomer.firstName}&lastName=${currentCustomer.lastName}&email=${currentCustomer.email}&user_id=${currentCustomer.id}`,
                        '_blank'
                      );
                    }}
                  />
                )}
              </>
            </div>
            {previousScan && (
              <div className={styles.previousScan}>
                <Typography component="p" className={styles.scanBlockTitle}>
                  <span>Previous Scan Details:</span>
                </Typography>
                <div className={styles.scan}>
                  <Typography component="p" className={styles.scanDetails}>
                    <span>Date:</span> {formatDateWithTime(previousScan.date)}
                  </Typography>
                  <Typography component="p" className={styles.scanDetails}>
                    <span>Status:</span>{' '}
                    {previousScan.status === ScanStatus.Complete
                      ? 'Complete'
                      : previousScan.status === ScanStatus.Failed
                      ? 'Failed'
                      : previousScan.status === ScanStatus.WaitingForData
                      ? 'Waiting for Data'
                      : 'Processing'}
                  </Typography>
                </div>
              </div>
            )}
          </View>
        </Grid>
        <div className={styles.buttonsContainer}>
          {shopLink && !inIframe && (
            <div className={styles.continueShoppingButton}>
              {confirm_deposit_checkbox === 'true' && (
                <div className={`${styles.confirmDeposit} custom-checkbox`}>
                  <CheckboxField
                    value=""
                    label={confirmDepositLabel}
                    name="confirmDeposit"
                    onChange={(e) => setConfirmDepositValue(e.target.checked)}
                  />
                </div>
              )}
              <Button
                buttonType="secondary"
                isDisabled={
                  confirm_deposit_checkbox === 'true' && !confirmDepositValue
                }
                text="Go to Shop"
                onClick={() => {
                  ReactGA.event('go_to_shop');
                  if (currentCustomer.firstName && currentCustomer.lastName) {
                    window.location.assign(
                      `${shopLink}&first_name=${currentCustomer.firstName}&last_name=${currentCustomer.lastName}`
                    );
                  } else {
                    window.location.assign(shopLink);
                  }
                }}
              />
            </div>
          )}
        </div>
      </ContentBlock>
      <QuestionnaireIframe
        isModalType
        show={isShowQuestionnaire}
        closeHandler={() => setIsShowQuestionnaire(false)}
        customer={currentCustomer}
      />
    </>
  );
};
